import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { PermissionService } from '../../../shared/services/apis/platform/permission.service';
import { UserPermissionService } from '../../../shared/services/apis/platform/user-permission.service';
import { UserService } from '../../../shared/services/apis/platform/user.service';
import { UserPermissionRequest } from '../../../shared/services/requests/platform/user-permission.request';
import { UserPermissionResponse } from '../../../shared/services/responses/platform/user-permission.response';
import { PermissionStruct } from '../../../shared/services/structs/permission.struct';
import { UserSimpleStruct } from '../../../shared/services/structs/user-list.response';

@Component({
  selector: 'app-user-permission-new',
  standalone: true,
  templateUrl: './user-permission-new.component.html',
  styleUrls: ['./user-permission-new.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MenuComponent,
    FooterComponent,
    LoginValidationComponent,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPermissionNewComponent implements OnInit {
  isLoading = false;
  isEditMode = false;

  // dados base
  allUsers: UserSimpleStruct[] = [];
  filteredUsers: UserSimpleStruct[] = [];
  userSearch = '';

  allPermissions: PermissionStruct[] = [];
  selectedPermissions: PermissionStruct[] = [];

  selectedUser: UserSimpleStruct | null = null;
  lockUserSelect = false; // trave para impedir troca quando necessário

  // opções não selecionadas
  get permissionOptions(): PermissionStruct[] {
    const selectedIds = new Set(this.selectedPermissions.map(p => p.idPermission));
    return this.allPermissions.filter(p => !selectedIds.has(p.idPermission));
  }

  form = this.fb.group({
    idUser: [0, [Validators.required, Validators.min(1)]],
    listIdPermission: [[] as number[]]
  });

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private userPermissionService: UserPermissionService,
    private permissionService: PermissionService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) return;
    this.bootstrap();
  }

  private bootstrap(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    // Carrega permissões e usuários em paralelo
    Promise.all([
      this.permissionService.list().toPromise(),
      this.userService.listActive().toPromise()
    ]).then(([permRes, userRes]) => {
      const pRes = permRes as any;
      const uRes = userRes as any;

      if (pRes?.isError) throw new Error(pRes.errorDescription || 'Falha ao carregar permissões.');
      if (uRes?.isError) throw new Error(uRes.errorDescription || 'Falha ao carregar usuários.');

      this.allPermissions = (pRes?.listPermission || []) as PermissionStruct[];
      this.allUsers = (uRes?.listUser || []).filter((u: UserSimpleStruct) => u.isActive);

      // filtro inicial (sem selecionado)
      this.applyUserFilter('');

      // checa rota: se veio idUser, pré-carrega
      const idUser = Number(this.route.snapshot.paramMap.get('idUser') || 0);
      if (idUser > 0) {
        this.isEditMode = true;
        this.loadUserPermissions(idUser);
      } else {
        this.isEditMode = false;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    }).catch((err) => {
      this.alertService.show('Erro', err?.message || 'Falha ao iniciar a tela.', AlertType.error);
      this.isLoading = false;
      this.cdr.markForCheck();
    });
  }

  private loadUserPermissions(idUser: number): void {
    this.userPermissionService.getByUser(idUser, true).subscribe({
      next: (res: UserPermissionResponse) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Falha ao buscar permissões do usuário.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        const up = res.userPermissionStruct;
        // seleciona usuário
        this.selectedUser = {
          idUser: up.idUser,
          userName: up.userName || '',
          userEmail: up.userEmail || '',
          isActive: up.isActive
        };
        this.form.controls.idUser.setValue(this.selectedUser.idUser);
        this.lockUserSelect = true; // em modo edição, travar seleção (ajuste se desejar permitir)

        // monta permissões selecionadas (IDs -> objetos)
        const ids = (up.listUserPermission || []).map(x => x.idPermission);
        this.selectedPermissions = this.allPermissions.filter(p => ids.includes(p.idPermission));
        this.form.controls.listIdPermission.setValue(ids);

        // retira o usuário selecionado da lista
        this.applyUserFilter(this.userSearch);

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alertService.show('Erro', 'Erro inesperado ao carregar dados do usuário.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ---- Usuário ----
  onUserSearch(value: string) {
    this.userSearch = value;
    this.applyUserFilter(value);
  }

  private applyUserFilter(value: string) {
    const v = (value || '').toLowerCase();
    this.filteredUsers = this.allUsers
      .filter(u => !this.selectedUser || u.idUser !== this.selectedUser.idUser)
      .filter(u => (u.userName?.toLowerCase().includes(v) || u.userEmail?.toLowerCase().includes(v)));
    this.cdr.markForCheck();
  }

  onUserSelected(u: UserSimpleStruct) {
    this.selectedUser = u;
    this.form.controls.idUser.setValue(u.idUser);
    this.applyUserFilter(this.userSearch);
    this.cdr.markForCheck();
  }

  clearUser() {
    if (this.lockUserSelect) return;
    this.selectedUser = null;
    this.form.controls.idUser.setValue(0);
    this.applyUserFilter(this.userSearch);
    this.cdr.markForCheck();
  }

  trackByUser = (_: number, u: UserSimpleStruct) => u?.idUser ?? _;

  // ---- Permissões ----
  onAddPermission(p: PermissionStruct) {
    if (!p) return;
    if (!this.selectedPermissions.find(x => x.idPermission === p.idPermission)) {
      this.selectedPermissions = [...this.selectedPermissions, p];
      this.syncPermissionIds();
      this.cdr.markForCheck();
    }
  }

  removePermission(p: PermissionStruct) {
    this.selectedPermissions = this.selectedPermissions.filter(x => x.idPermission !== p.idPermission);
    this.syncPermissionIds();
    this.cdr.markForCheck();
  }

  trackByPermission = (_: number, p: PermissionStruct) => p?.idPermission ?? _;
  trackByPermissionId = (_: number, p: PermissionStruct) => p?.idPermission ?? _;

  private syncPermissionIds() {
    const ids = this.selectedPermissions.map(x => x.idPermission);
    this.form.controls.listIdPermission.setValue(ids);
  }

  // ---- Ações ----
  canSave(): boolean {
    return !!this.form.valid && !!this.form.value.idUser;
  }

  save(): void {
    if (!this.canSave()) {
      this.alertService.show('Atenção', 'Selecione um usuário e ao menos uma permissão (se aplicável).', AlertType.warning);
      return;
    }

    const req = new UserPermissionRequest();
    req.idUser = this.form.value.idUser!;
    req.listIdPermission = this.form.value.listIdPermission || [];

    this.isLoading = true;
    this.userPermissionService.upsert(req).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Falha ao salvar permissões.', AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Permissões salvas com sucesso.', AlertType.success);
          this.goBack();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alertService.show('Erro', 'Erro inesperado ao salvar.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  deleteAll(): void {
    if (!this.selectedUser?.idUser) return;
    const ok = confirm('Confirma remover TODAS as permissões deste usuário?');
    if (!ok) return;

    this.isLoading = true;
    this.userPermissionService.deleteByUser(this.selectedUser.idUser).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Falha ao excluir permissões.', AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Permissões removidas.', AlertType.success);
          this.goBack();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alertService.show('Erro', 'Erro inesperado ao excluir.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  goBack(): void {
    this.router.navigateByUrl('/usuarios-admin');
  }
}
